import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './MultiSelect.css';

/**
 * Multi-select dropdown for M2M relationships (e.g., Products)
 * Shows selected items as removable pills, with a filterable dropdown.
 */
const MultiSelect = ({
  label,
  name,
  options = [],
  value = [],
  onChange,
  error,
  placeholder = 'Select items...',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef(null);
  const filterInputRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus filter input when dropdown opens
  useEffect(() => {
    if (isOpen && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (optionId) => {
    const newValue = value.includes(optionId)
      ? value.filter((v) => v !== optionId)
      : [...value, optionId];
    onChange(name, newValue);
  };

  const handleRemove = (optionId) => {
    onChange(name, value.filter((v) => v !== optionId));
  };

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(filter.toLowerCase())
  );

  const selectedOptions = options.filter((opt) => value.includes(opt.id));

  return (
    <div className={`multi-select ${className}`} ref={containerRef}>
      {label && (
        <label className="multi-select__label">{label}</label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        className={`multi-select__trigger ${isOpen ? 'multi-select__trigger--open' : ''} ${error ? 'multi-select__trigger--error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="multi-select__trigger-text">
          {value.length === 0
            ? placeholder
            : `${value.length} selected`}
        </span>
        <svg className={`multi-select__chevron ${isOpen ? 'multi-select__chevron--open' : ''}`} viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6L8 10L12 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="multi-select__dropdown" role="listbox" aria-multiselectable="true">
          <div className="multi-select__filter-wrap">
            <input
              ref={filterInputRef}
              type="text"
              className="multi-select__filter"
              placeholder="Filter..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter options"
            />
          </div>
          <ul className="multi-select__options">
            {filteredOptions.length === 0 ? (
              <li className="multi-select__empty">No matches</li>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = value.includes(opt.id);
                return (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={isSelected}
                    className={`multi-select__option ${isSelected ? 'multi-select__option--selected' : ''}`}
                    onClick={() => handleToggle(opt.id)}
                  >
                    <span className="multi-select__option-box" aria-hidden="true">
                      {isSelected && (
                        <svg viewBox="0 0 12 12" className="multi-select__option-check">
                          <path d="M2.5 6L5 8.5L9.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="multi-select__option-name">{opt.name}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}

      {/* Selected pills */}
      {selectedOptions.length > 0 && (
        <div className="multi-select__pills">
          {selectedOptions.map((opt) => (
            <span key={opt.id} className="multi-select__pill">
              <span className="multi-select__pill-text">{opt.name}</span>
              <button
                type="button"
                className="multi-select__pill-remove"
                onClick={() => handleRemove(opt.id)}
                aria-label={`Remove ${opt.name}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {error && <div className="multi-select__error">{error}</div>}
    </div>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

export default MultiSelect;
